import type { Server, ServerAction, ServerStatusReply } from "@mc/types"
import { apiRequest } from "./httpClient.js"

/** FR section 8, `/dashboard` y `/servers`: lista de servidores propios. */
export function listServers(): Promise<{ servers: Server[] }> {
  return apiRequest("/api/v1/servers")
}

/** FR section 8, `/servers/[id]`. */
export function getServer(serverId: string): Promise<{ server: Server }> {
  return apiRequest(`/api/v1/servers/${serverId}`)
}

/**
 * FR section 15 (estado, jugadores, TPS, RAM, uptime), backed by
 * GET /api/v1/servers/:serverId/status (services/gateway/src/routes/
 * servers.ts), which answers via servers.queries.status over NATS
 * request/reply. `state: "unknown"` with a non-null `error` of
 * "crafty_down" means server-manager answered but Crafty itself did not;
 * every other field on ServerStatusReply is `null` in that case.
 */
export function getServerStatus(serverId: string): Promise<{ status: ServerStatusReply }> {
  return apiRequest(`/api/v1/servers/${serverId}/status`)
}

/**
 * TODO.md "Crear servidores desde la app". `installMethod: "download_jar"`
 * es el unico soportado hoy. `serverSoftware`/`minecraftVersion`/
 * `loaderVersion` vienen del catalogo dinamico (ver api/serverCatalog.ts)
 * o de texto libre para software que no tiene catalogo dinamico -- ver
 * ServersPage.tsx.
 */
export interface CreateServerInput {
  name: string
  installMethod: "download_jar"
  minecraftVersion: string
  serverSoftware: string
  loaderVersion?: string | null
  memMinGb: number
  memMaxGb: number
  port: number
}

export function createServer(input: CreateServerInput): Promise<{ serverId: string }> {
  return apiRequest("/api/v1/servers", { method: "POST", body: input })
}

/**
 * start/stop/restart/kill son asincronicos del lado del Gateway (202,
 * ver services/gateway/src/routes/servers.ts): esta funcion resuelve en
 * cuanto el comando fue aceptado y publicado a NATS, no cuando el
 * servidor efectivamente cambia de estado. El resultado real llega mas
 * tarde via WebSocket (FR seccion 14), todavia no cableado en apps/web.
 */
export function runServerAction(
  serverId: string,
  action: ServerAction,
  force = false,
): Promise<{ accepted: boolean }> {
  return apiRequest(`/api/v1/servers/${serverId}/${action}`, { method: "POST", body: { force } })
}

/** FR section 11 ("Personalizacion del servidor"): name, description, icon, public IP. */
export function updateServer(
  serverId: string,
  input: { name?: string; description?: string | null; iconUrl?: string | null; publicIp?: string | null },
): Promise<{ server: Server }> {
  return apiRequest(`/api/v1/servers/${serverId}`, { method: "PATCH", body: input })
}

/**
 * Borra un servidor (Permission.SERVER_DELETE), backed by
 * DELETE /api/v1/servers/:serverId (services/gateway/src/routes/servers.ts),
 * que borra en Crafty primero y en la DB local despues (ver el handler de
 * server-manager). `deleted: true` con `error` no-nulo significa que
 * Crafty ya borro el servidor de verdad (irreversible) pero el registro
 * local tuvo un problema -- la UI debe tratar esto como "borrado" (el
 * servidor ya no existe en Crafty) y mostrar el error como una advertencia
 * secundaria, no como un fallo del borrado completo.
 */
export function deleteServer(serverId: string): Promise<{ deleted: boolean; error: string | null }> {
  return apiRequest(`/api/v1/servers/${serverId}`, { method: "DELETE" })
}
